import React, { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import '../styles/calendar-audio-recorder.css';

const CalendarAudioRecorder = ({ onAudioAdded, selectedAudioIds = [], onSelectionChange }) => {
  const { user } = useAuth();
  const [isRecording, setIsRecording] = useState(false);
  
  // Component debug info for recording functionality
  console.log('CalendarAudioRecorder: Component loaded for recording and library access');
  const [audioBlob, setAudioBlob] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [availableClips, setAvailableClips] = useState([]);
  const [saving, setSaving] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioRef = useRef(null);
  const chunksRef = useRef([]);

  // Start recording
  const startRecording = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    console.log('Start recording - event:', e);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      chunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      console.log('Recording started successfully');
    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Could not access microphone. Please check permissions.');
    }
  };

  // Stop recording
  const stopRecording = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    console.log('Stop recording - event:', e);
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // Play recorded audio
  const playRecording = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (audioBlob && audioRef.current) {
      audioRef.current.src = URL.createObjectURL(audioBlob);
      audioRef.current.play();
      setIsPlaying(true);
      audioRef.current.onended = () => setIsPlaying(false);
    }
  };

  // Save recording
  const saveRecording = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!audioBlob) return;
    
    console.log('Saving recording - event:', e);
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');
      formData.append('name', `Calendar Recording ${new Date().toLocaleString()}`);
      formData.append('category', 'calendar');
      formData.append('userId', user.id);
      formData.append('userRole', user.role);

      const response = await fetch('/api/audio-clips?operation=uploadAudio', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const uploadResult = await response.json();
        console.log('🎉 File uploaded successfully:', uploadResult);
        
        // Now create the audio clip database record
        const clipData = {
          name: `Calendar Recording ${new Date().toLocaleString()}`,
          description: 'Audio recording from calendar entry',
          fileUrl: uploadResult.fileUrl,
          fileName: uploadResult.fileName,
          fileSize: uploadResult.fileSize,
          mimeType: uploadResult.mimeType,
          category: 'calendar',
          creatorUserId: user.id
        };
        
        const clipResponse = await fetch('/api/audio-clips?operation=createAudioClip', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(clipData)
        });
        
        if (clipResponse.ok) {
          const clipResult = await clipResponse.json();
          onAudioAdded(clipResult);
          setAudioBlob(null);
          console.log('🎉 Audio clip created successfully:', clipResult);
        } else {
          const clipError = await clipResponse.text();
          console.error('❌ Failed to create audio clip record:', clipError);
          throw new Error(`Failed to create audio clip: ${clipError}`);
        }
      } else {
        const errorText = await response.text();
        console.error('❌ API Response Status:', response.status);
        console.error('❌ API Response Headers:', [...response.headers.entries()]);
        console.error('❌ API Response Body:', errorText);
        throw new Error(`API Error ${response.status}: ${errorText}`);
      }
    } catch (error) {
      console.error('Error saving recording:', error);
      alert('Failed to save recording');
    } finally {
      setSaving(false);
    }
  };

  // Load available clips
  const loadLibrary = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    console.log('Loading library - event:', e);
    try {
      const response = await fetch(`/api/audio-clips?operation=getAudioClips&userId=${user.id}`);
      if (response.ok) {
        const clips = await response.json();
        setAvailableClips(clips || []);
        setShowLibrary(true);
        console.log('Library loaded successfully, showLibrary set to true');
      }
    } catch (error) {
      console.error('Error loading audio library:', error);
    }
  };

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', file.name.replace(/\.[^/.]+$/, ''));
      formData.append('category', 'calendar');
      formData.append('userId', user.id);
      formData.append('userRole', user.role);

      const response = await fetch('/api/audio-clips?operation=uploadAudio', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const uploadResult = await response.json();
        console.log('🎉 File uploaded successfully:', uploadResult);
        
        // Now create the audio clip database record
        const clipData = {
          name: file.name.replace(/\.[^/.]+$/, ''),
          description: 'Audio file uploaded from calendar entry',
          fileUrl: uploadResult.fileUrl,
          fileName: uploadResult.fileName,
          fileSize: uploadResult.fileSize,
          mimeType: uploadResult.mimeType,
          category: 'calendar',
          creatorUserId: user.id
        };
        
        const clipResponse = await fetch('/api/audio-clips?operation=createAudioClip', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(clipData)
        });
        
        if (clipResponse.ok) {
          const clipResult = await clipResponse.json();
          onAudioAdded(clipResult);
          console.log('🎉 Audio clip created successfully:', clipResult);
        } else {
          const clipError = await clipResponse.text();
          console.error('❌ Failed to create audio clip record:', clipError);
          throw new Error(`Failed to create audio clip: ${clipError}`);
        }
      } else {
        const errorText = await response.text();
        console.error('❌ Upload API Response Status:', response.status);
        console.error('❌ Upload API Response Body:', errorText);
        throw new Error(`Upload API Error ${response.status}: ${errorText}`);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file');
    } finally {
      setSaving(false);
      event.target.value = '';
    }
  };

  return (
    <div className="calendar-audio-recorder" onClick={(e) => e.stopPropagation()}>
      {!showLibrary ? (
        <>
          {/* Recording Interface */}
          <div className="recording-section">
            <h4>🎤 Add Audio</h4>
            
            {!audioBlob ? (
              <div className="record-controls">
                <button
                  className={`record-button ${isRecording ? 'recording' : ''}`}
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={saving}
                >
                  {isRecording ? '⏹️ Stop' : '🎤 Record'}
                </button>
                <span className="record-hint">
                  {isRecording ? 'Recording... Click stop when done' : 'Click to start recording'}
                </span>
              </div>
            ) : (
              <div className="playback-controls">
                <button 
                  className="play-button"
                  onClick={playRecording}
                  disabled={isPlaying}
                >
                  {isPlaying ? '⏸️ Playing' : '▶️ Play'}
                </button>
                <button 
                  className="save-button"
                  onClick={saveRecording}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : '💾 Save'}
                </button>
                <button 
                  className="discard-button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setAudioBlob(null);
                  }}
                >
                  🗑️ Discard
                </button>
              </div>
            )}
            
            <audio ref={audioRef} style={{ display: 'none' }} />
          </div>

          {/* Upload/Library Options */}
          <div className="alternative-options">
            <div className="option-divider">or</div>
            
            <div className="upload-section">
              <input
                type="file"
                accept="audio/*"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
                id="audio-upload"
                disabled={saving}
              />
              <label htmlFor="audio-upload" className="upload-button" onClick={(e) => e.stopPropagation()}>
                📁 Upload File
              </label>
            </div>
            
            <button 
              className="library-button"
              onClick={loadLibrary}
            >
              📚 Choose from Library
            </button>
          </div>
        </>
      ) : (
        /* Library Selection */
        <div className="library-section">
          <div className="library-header">
            <h4>📚 Audio Library</h4>
            <button 
              className="back-button"
              onClick={() => setShowLibrary(false)}
            >
              ← Back
            </button>
          </div>
          
          <div className="clips-list">
            {availableClips.map(clip => (
              <div
                key={clip.id}
                className={`clip-item ${selectedAudioIds.includes(clip.id) ? 'selected' : ''}`}
                onClick={() => onSelectionChange(clip.id)}
              >
                <input
                  type="checkbox"
                  checked={selectedAudioIds.includes(clip.id)}
                  onChange={() => onSelectionChange(clip.id)}
                />
                <span className="clip-name">{clip.name || clip.title}</span>
                <span className="clip-duration">
                  {clip.duration_seconds ? `${Math.round(clip.duration_seconds)}s` : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarAudioRecorder;